import { API_DEFAULTS } from './config/apiDefaults.js';

function generateUUID() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

function getCookie(name) {
    const raw = document.cookie || "";
    const parts = raw.split("; ");
    for (const part of parts) {
        if (!part) continue;
        const idx = part.indexOf("=");
        if (idx === -1) continue;
        const key = part.slice(0, idx);
        if (key === name) {
            return decodeURIComponent(part.slice(idx + 1));
        }
    }
    return "";
}

export function getCsrfToken() {
    return getCookie("mosaic_csrf");
}

function getHeaders(isJson = true) {
    const headers = {};
    const csrfToken = getCsrfToken();
    if (csrfToken) headers['x-csrf-token'] = csrfToken;
    if (isJson) headers['Content-Type'] = 'application/json';
    return headers;
}

async function handleResponse(res) {
    if (!res.ok) {
        const extractMessage = (value) => {
            if (value == null) return '';
            if (typeof value === 'string') return value.trim();
            if (Array.isArray(value)) {
                if (!value.length) return '';
                const first = value[0];
                const nested = extractMessage(first);
                if (nested) return nested;
                try {
                    return JSON.stringify(value);
                } catch (_e) {
                    return '';
                }
            }
            if (typeof value === 'object') {
                const loc = Array.isArray(value.loc)
                    ? value.loc.filter(Boolean).join('.')
                    : '';
                const msg = typeof value.msg === 'string'
                    ? value.msg
                    : (typeof value.message === 'string' ? value.message : '');
                if (msg) return loc ? `${loc}: ${msg}` : msg;
                if (typeof value.detail === 'string') return value.detail;
                if (typeof value.error === 'string') return value.error;
                try {
                    return JSON.stringify(value);
                } catch (_e) {
                    return '';
                }
            }
            return String(value);
        };

        let msg = `HTTP ${res.status}`;
        let payload = null;
        try {
            payload = await res.json();
            const detailMessage = extractMessage(payload.detail);
            const messageMessage = extractMessage(payload.message);
            const errorMessage = extractMessage(payload.error);
            msg = detailMessage || messageMessage || errorMessage || msg;
        } catch (e) {}
        const err = new Error(msg);
        err.status = res.status;
        err.payload = payload;
        const retryAfter = res.headers.get('Retry-After');
        if (retryAfter) {
            const parsed = parseInt(retryAfter, 10);
            err.retryAfter = Number.isFinite(parsed) ? parsed : retryAfter;
        }
        throw err;
    }
    return res.json();
}

// --- API CALLS ---

export async function getLibraries() {
    const res = await fetch('/libraries', { headers: getHeaders() });
    return handleResponse(res);
}

export async function createLibrary(name, isPublic) {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('is_public', isPublic);
    const res = await fetch('/create-library', {
        method: 'POST',
        headers: getHeaders(false),
        body: formData
    });
    return handleResponse(res);
}

export async function renameLibrary(id, name) {
    const formData = new FormData();
    formData.append('name', name);
    const res = await fetch(`/rename-library/${id}`, {
        method: 'POST', headers: getHeaders(false), body: formData
    });
    return handleResponse(res);
}

export async function setLibraryPrivacy(id, isPublic) {
    const formData = new FormData();
    formData.append('is_public', isPublic);
    const res = await fetch(`/set-library-privacy/${id}`, {
        method: 'POST', headers: getHeaders(false), body: formData
    });
    return handleResponse(res);
}

export async function deleteLibrary(id) {
    const res = await fetch(`/delete-library/${id}`, { method: 'DELETE', headers: getHeaders() });
    return handleResponse(res);
}

export async function getLibraryPreview(id, limit = API_DEFAULTS.LIBRARY_PREVIEW_LIMIT, offset = API_DEFAULTS.LIBRARY_PREVIEW_OFFSET) {
    const res = await fetch(`/library-preview/${id}?limit=${limit}&offset=${offset}`, { headers: getHeaders() });
    return handleResponse(res);
}

export async function uploadToLibrary(libId, file) {
    const formData = new FormData();
    const filename = (file && file.name) ? file.name : "tile.jpg";
    formData.append('file', file, filename);
    const res = await fetch(`/upload-to-library/${libId}`, {
        method: 'POST', headers: getHeaders(false), body: formData
    });
    return handleResponse(res);
}

export async function releaseLibraryUploadSession(sessionId, force = false, options = {}) {
    if (!sessionId) return { status: 'ok', released: false };
    const keepalive = Boolean(options && options.keepalive);
    const qs = force ? '?force=1' : '';
    const res = await fetch(`/library/upload-session/release${qs}`, {
        method: 'POST',
        headers: { ...getHeaders(false), 'x-upload-session': String(sessionId) },
        credentials: 'include',
        keepalive,
    });
    if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
            const payload = await res.json();
            msg = payload.detail || payload.message || payload.error || msg;
        } catch (e) {}
        throw new Error(msg);
    }
    try {
        return await res.json();
    } catch (e) {
        return { status: 'ok', released: false };
    }
}

export async function deleteTile(libId, filename) {
    const res = await fetch(`/library/${libId}/tile/${filename}`, {
        method: 'DELETE',
        headers: getHeaders()
    });
    return handleResponse(res);
}

export async function uploadTarget(file) {
    const jobId = generateUUID().substring(0, 8);
    const formData = new FormData();
    formData.append('file', file, file.name || "target.jpg");

    const res = await fetch(`/upload-target/${jobId}`, {
        method: 'POST', headers: getHeaders(false), body: formData
    });
    const data = await handleResponse(res);
    return { ...data, jobId: (data && data.job_id) ? data.job_id : jobId };
}

// --- NEW: Fork Job ---
export async function forkJob(jobId, options = {}) {
    const params = new URLSearchParams();
    const sourcePhotoId = String(options?.sourcePhotoId || '').trim();
    if (sourcePhotoId) params.set('source_photo_id', sourcePhotoId);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const res = await fetch(`/job/${jobId}/fork${suffix}`, {
        method: 'POST',
        headers: getHeaders()
    });
    return handleResponse(res);
}

export async function runJob(jobId, settings) {
    if (!jobId) jobId = generateUUID().substring(0, 8);
    const formData = new FormData();
    for (const key in settings) formData.append(key, settings[key]);

    const res = await fetch(`/run-job/${jobId}`, {
        method: 'POST', headers: getHeaders(false), body: formData
    });
    return handleResponse(res);
}

export async function cancelJobById(jobId) {
    const res = await fetch(`/cancel-job/${jobId}`, { method: 'POST', headers: getHeaders() });
    return handleResponse(res);
}

export async function deleteJob(jobId) {
    const res = await fetch(`/job/${jobId}`, {
        method: 'DELETE',
        headers: getHeaders()
    });
    return handleResponse(res);
}

export async function getStatus(jobId) {
    if(!jobId) return { phase: "unknown" };
    const res = await fetch(`/status/${jobId}`, { headers: getHeaders(false) });
    return handleResponse(res);
}

export async function getSocialVideoStatus(jobId) {
    if (!jobId) return { status: 'missing' };
    const res = await fetch(`/social-video-status/${jobId}`, { headers: getHeaders(false) });
    return handleResponse(res);
}

export async function getActiveJob() {
    const res = await fetch('/active-job', { headers: getHeaders(false) });
    return handleResponse(res);
}

export async function getHistory(limit = API_DEFAULTS.HISTORY_LIMIT, cursor = null, offset = API_DEFAULTS.HISTORY_OFFSET) {
    const safeLimit = Math.max(1, Number(limit || API_DEFAULTS.HISTORY_LIMIT));
    let cursorValue = cursor;
    let offsetValue = offset;
    if (cursorValue != null && typeof cursorValue === 'number' && offset === API_DEFAULTS.HISTORY_OFFSET) {
        offsetValue = cursorValue;
        cursorValue = null;
    }

    const params = new URLSearchParams();
    params.set('limit', String(safeLimit));
    if (cursorValue) {
        params.set('cursor', String(cursorValue));
    } else {
        params.set('offset', String(Math.max(0, Number(offsetValue || 0))));
    }

    const res = await fetch(`/get-history?${params.toString()}`, { headers: getHeaders(false) });
    if (!res.ok) return { items: [] };
    return res.json();
}

export async function getRecentPhotos(limit = 40) {
    const safeLimit = Math.max(1, Math.min(200, Number(limit || 40)));
    const res = await fetch(`/recent-photos?limit=${safeLimit}`, { headers: getHeaders(false) });
    if (!res.ok) return [];
    return res.json();
}

export async function getSourcePhoto(sourceId) {
    const safeId = String(sourceId || '').trim();
    if (!safeId) return null;
    const res = await fetch(`/source-photo/${encodeURIComponent(safeId)}`, { headers: getHeaders(false) });
    return handleResponse(res);
}

export async function hideRecentPhoto(sourceId) {
    const payload = { source_id: String(sourceId || "") };
    const res = await fetch('/recent-photos/hide', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(payload),
    });
    return handleResponse(res);
}

export async function logEvent(event, metadata = {}) {
    const payload = {
        event: String(event || ''),
        metadata: metadata || {},
    };
    const res = await fetch('/log-event', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(payload),
    });
    return handleResponse(res);
}

function _normalizeSiteLabel(value, fallback = 'unknown') {
    const normalized = String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_]+/g, '_')
        .replace(/^_+|_+$/g, '');
    return normalized || fallback;
}

function _deriveSiteSource(explicit = '') {
    const direct = _normalizeSiteLabel(explicit, '');
    if (direct) return direct;
    try {
        const params = new URLSearchParams(window.location.search || '');
        const utmSource = _normalizeSiteLabel(params.get('utm_source') || '', '');
        if (utmSource) return utmSource;
    } catch (e) {}
    const referrer = String(document.referrer || '').toLowerCase();
    if (!referrer) return 'direct';
    if (referrer.includes('chatgpt.com')) return 'chatgpt';
    if (referrer.includes('google.')) return 'google';
    if (referrer.includes('bing.')) return 'bing';
    if (referrer.includes('instagram.')) return 'instagram';
    if (referrer.includes('tiktok.')) return 'tiktok';
    if (referrer.includes('facebook.') || referrer.includes('fb.')) return 'facebook';
    if (referrer.includes('pinterest.')) return 'pinterest';
    return 'referral';
}

export async function logSiteEvent(event, payload = {}) {
    const query = new URLSearchParams();
    query.set('event', _normalizeSiteLabel(event, 'unknown'));
    query.set('page', _normalizeSiteLabel(payload.page, 'app'));
    query.set('cta', _normalizeSiteLabel(payload.cta, 'none'));
    query.set('source', _deriveSiteSource(payload.source));
    if (payload.utm_source) {
        query.set('utm_source', String(payload.utm_source).trim().slice(0, 128));
    }
    if (payload.utm_campaign) {
        query.set('utm_campaign', String(payload.utm_campaign).trim().slice(0, 128));
    }
    const res = await fetch('/site-event?' + query.toString(), {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        keepalive: true,
    });
    if (!res.ok) {
        throw new Error(`site-event HTTP ${res.status}`);
    }
    try {
        return await res.json();
    } catch (e) {
        return { status: 'ok' };
    }
}

function normalizeUiFlowLabel(value, fallback = 'unknown') {
    const normalized = String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_]+/g, '_')
        .replace(/^_+|_+$/g, '');
    return normalized || fallback;
}

export async function logUiFlow(stage, action, outcome = 'ok', metadata = {}, durationMs = null) {
    const payload = {
        stage: normalizeUiFlowLabel(stage, 'unknown'),
        action: normalizeUiFlowLabel(action, 'unknown'),
        outcome: normalizeUiFlowLabel(outcome, 'ok'),
        metadata: metadata || {},
    };
    if (durationMs != null) {
        const val = Number(durationMs);
        if (Number.isFinite(val) && val >= 0) {
            payload.duration_ms = Math.round(val);
        }
    }
    return logEvent('ui_flow', payload);
}

export async function getManifest(jobId) {
    if(!jobId) return {};
    const res = await fetch(`/get-manifest/${jobId}`, { headers: getHeaders(false) });
    if(res.ok) return res.json();
    return {};
}

export async function getManifestByKey(key, options = {}) {
    if(!key) return {};
    const includeMap = options && options.includeMap === false ? '0' : '1';
    const res = await fetch(
        `/get-manifest-by-key?key=${encodeURIComponent(key)}&include_map=${includeMap}`,
        { headers: getHeaders(false) }
    );
    if(res.ok) return res.json();
    return {};
}

export async function getBillingStatus() {
    const res = await fetch('/billing/status', { headers: getHeaders(false) });
    return handleResponse(res);
}

export async function getBillingAccountSummary() {
    const res = await fetch('/billing/account-summary', { headers: getHeaders(false) });
    return handleResponse(res);
}

export async function getBillingPlanComparison() {
    const res = await fetch('/billing/plan-comparison', { headers: getHeaders(false) });
    return handleResponse(res);
}

export async function createExportIntent(jobId, longEdgePx) {
    const res = await fetch('/billing/export-intents', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
            job_id: String(jobId || ''),
            long_edge_px: Number(longEdgePx || 0),
        }),
    });
    return handleResponse(res);
}

export async function createSubscriptionCheckout(planCode) {
    const res = await fetch('/billing/subscription-checkout', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
            plan_code: String(planCode || ''),
        }),
    });
    return handleResponse(res);
}

export async function finalizePreflight(baseJobId, longEdgePx) {
    const res = await fetch('/finalize/preflight', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
            base_job_id: String(baseJobId || ''),
            long_edge_px: Number(longEdgePx || 0),
        }),
    });
    return handleResponse(res);
}

export async function finalizeOptions(baseJobId) {
    const res = await fetch(`/finalize/options/${encodeURIComponent(String(baseJobId || ''))}`, {
        headers: getHeaders(false),
    });
    return handleResponse(res);
}

export async function finalizeStart(baseJobId, longEdgePx) {
    const res = await fetch('/finalize/start', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
            base_job_id: String(baseJobId || ''),
            long_edge_px: Number(longEdgePx || 0),
        }),
    });
    return handleResponse(res);
}

export async function finalizeStatus(finalRunId) {
    const res = await fetch(`/finalize/status/${encodeURIComponent(String(finalRunId || ''))}`, {
        headers: getHeaders(false),
    });
    return handleResponse(res);
}
